import { useEffect } from "react";
import { Lock, Check } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { StatusBadge } from "@/components/StatusBadge";
import { splitFeatures } from "@/components/PackageCards";
import { StatusSteps, getStage } from "@/components/dashboard/StatusSteps";
import { BankDetails } from "@/components/dashboard/BankDetails";
import { ReceiptUpload } from "@/components/dashboard/ReceiptUpload";
import { RewardsOverview, PackageSummary } from "@/components/dashboard/RewardsOverview";
import { MemberCard } from "@/components/dashboard/MemberCard";
import { ContactCard } from "@/components/dashboard/ContactCard";

const NOTICE = {
  awaiting: "border-sky-300 bg-sky-50 text-sky-900 dark:border-sky-800 dark:bg-sky-950/50 dark:text-sky-300",
  pending: "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-300",
  active: "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300",
  rejected: "border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-800 dark:bg-rose-950/50 dark:text-rose-300",
};
const MESSAGE = { awaiting: "msg_awaiting_payment", pending: "msg_pending", active: "msg_approved", rejected: "msg_rejected" };
const BADGE = { awaiting: "awaiting", pending: "pending", active: "approved", rejected: "rejected" };

const ActiveView = ({ user, pkg }) => {
  const { t, tr } = useLang();
  const features = splitFeatures(tr(pkg?.features));
  return (
    <div className="mt-8 grid gap-6 lg:grid-cols-12">
      <div className="animate-fade-up lg:col-span-5"><RewardsOverview user={user} pkg={pkg} /></div>
      <div className="animate-fade-up lg:col-span-7" style={{ animationDelay: "100ms" }}><MemberCard user={user} /></div>
      {features.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-6 lg:col-span-12">
          <p className="text-sm font-semibold">{t("package_features")}</p>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {features.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground"><Check className="mt-0.5 h-4 w-4 text-primary" />{f}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};

const PaymentView = ({ user, pkg }) => (
  <div className="mt-8 grid gap-6 lg:grid-cols-12">
    <div className="animate-fade-up space-y-8 rounded-2xl border border-border bg-card p-6 lg:col-span-7">
      <BankDetails amount={pkg?.price} />
      <div className="border-t border-border pt-6"><ReceiptUpload user={user} /></div>
    </div>
    <div className="animate-fade-up lg:col-span-5" style={{ animationDelay: "100ms" }}><PackageSummary user={user} pkg={pkg} /></div>
  </div>
);

export default function UserDashboard() {
  const { t } = useLang();
  const { user, refresh } = useAuth();
  const { settings } = useSettings();
  const pkg = settings?.packages?.find((p) => p.id === user.package_id);
  const stage = getStage(user);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:py-16" data-testid="user-dashboard">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">{t("dash_title")}</h1>
          <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground" data-testid="dashboard-readonly-note"><Lock className="h-4 w-4" />{t("dash_readonly")}</p>
        </div>
        <StatusBadge status={BADGE[stage]} large testId="user-status-badge" />
      </div>

      <StatusSteps stage={stage} />

      <div className={`animate-fade-up mt-6 rounded-2xl border p-5 font-semibold ${NOTICE[stage]}`} data-testid="dashboard-status-message">
        {t(MESSAGE[stage])}
      </div>

      {stage === "active" ? <ActiveView user={user} pkg={pkg} /> : <PaymentView user={user} pkg={pkg} />}

      <div className="mt-6"><ContactCard /></div>
    </div>
  );
}
